import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';

import type {
  Device,
  DeviceContextValue,
  DeviceCreateRequest,
  DeviceUpdateRequest,
  TracePoint,
} from '../types';
import { displaySettingsAPI } from '../utils/apiClient';
import { deviceService } from '../utils/deviceService';
import { LifeFinderError } from '../utils/errorHandling';
import logger from '../utils/logger';

import { useAuth } from './AuthContext';

/**
 * TypeScript-powered Device Context with Centralized Error Handling
 * Migrated from DeviceContext.js with full type safety and error management
 */

const DeviceContext = createContext<DeviceContextValue | null>(null);

/**
 * Custom hook to access device context with type safety
 * Throws error if used outside of provider
 */
export const useDevices = (): DeviceContextValue => {
  const context = useContext(DeviceContext);
  if (!context) {
    throw new Error('useDevices must be used within a DeviceProvider');
  }
  return context;
};

interface DeviceProviderProps {
  children: ReactNode;
}

/**
 * Device Provider Component with TypeScript and Error Handling
 * Replaces the old JavaScript version with type safety and centralized error management
 */
export const DeviceProvider: React.FC<DeviceProviderProps> = ({ children }) => {
  const { isAuthenticated } = useAuth();

  // Core state with TypeScript
  const [devices, setDevices] = useState<Device[]>([]);
  const [selectedDevice, setSelectedDevice] = useState<string | null>(null);
  const [, setTraceData] = useState<TracePoint[]>([]); // traceData is managed internally, not exposed
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [, setLastUpdateTime] = useState<Date | null>(null);

  // Filter states with types
  const [searchQuery, _setSearchQuery] = useState<string>('');
  const [alarmFilter, _setAlarmFilter] = useState<number>(0); // seconds, 0 = show all
  const [positionFilter, _setPositionFilter] = useState<number>(0);
  const [signalFilter, _setSignalFilter] = useState<number>(0);
  const [categoryFilter, _setCategoryFilter] = useState<string>('all'); // 'all' | 'lf_device' | 'lf_beacon' | 'lf_checkin' | 'lf_gateway'
  // Loaded from backend on auth; defaults to true so the map looks unchanged before the fetch resolves.
  const [showActiveIndicator, _setShowActiveIndicator] = useState<boolean>(true);
  const [showBatchTrackingButtons, _setShowBatchTrackingButtons] = useState<boolean>(true);
  const [showTraceButton, _setShowTraceButton] = useState<boolean>(true);
  const [geofenceFitboundsPadding, _setGeofenceFitboundsPadding] = useState<number>(50);
  const [geofencingEnabled, _setGeofencingEnabled] = useState<boolean>(false);
  const [showCheckinSummary, _setShowCheckinSummary] = useState<boolean>(true);
  const [showDeviceStatusBoxes, _setShowDeviceStatusBoxes] = useState<boolean>(false);
  const [box2MqttEnabled, _setBox2MqttEnabled] = useState<boolean>(true);
  const [webPushEnabled, _setWebPushEnabled] = useState<boolean>(false);
  const [emailNotificationsEnabled, _setEmailNotificationsEnabled] = useState<boolean>(false);
  const [smtpConfigured, _setSmtpConfigured] = useState<boolean>(false);
  const [emailAlarmTemplate, _setEmailAlarmTemplate] = useState<string>(
    'Larm {{action}} för: {{devices}}.'
  );
  const [relayDnilDeveui, _setRelayDnilDeveui] = useState<string>('');
  const [relayDnilChannels, _setRelayDnilChannels] = useState<number>(0);
  const [relayDnilSilenceMs, _setRelayDnilSilenceMs] = useState<number>(3 * 60 * 60 * 1000);

  // Checkpoint management
  const [currentCheckpoint, setCurrentCheckpoint] = useState<string | null>(() => {
    return window.localStorage.getItem('current-checkpoint');
  });

  // Wrapper functions with debug logging and error handling
  const setSearchQuery = useCallback(
    (value: string): void => {
      logger.debug(`DeviceContext: Setting searchQuery from "${searchQuery}" to "${value}"`);
      _setSearchQuery(value);
    },
    [searchQuery]
  );

  const setAlarmFilter = useCallback(
    (value: number): void => {
      logger.debug(`DeviceContext: Setting alarmFilter from ${alarmFilter} to ${value}`);
      _setAlarmFilter(value);
    },
    [alarmFilter]
  );

  const setPositionFilter = useCallback(
    (value: number): void => {
      logger.debug(`DeviceContext: Setting positionFilter from ${positionFilter} to ${value}`);
      _setPositionFilter(value);
    },
    [positionFilter]
  );

  const setSignalFilter = useCallback(
    (value: number): void => {
      logger.debug(`DeviceContext: Setting signalFilter from ${signalFilter} to ${value}`);
      _setSignalFilter(value);
    },
    [signalFilter]
  );

  const setCategoryFilter = useCallback(
    (value: string): void => {
      logger.debug(`DeviceContext: Setting categoryFilter from "${categoryFilter}" to "${value}"`);
      _setCategoryFilter(value);
    },
    [categoryFilter]
  );

  /** Persists the setting to the backend and updates local state. */
  const setShowActiveIndicator = useCallback(async (value: boolean): Promise<void> => {
    logger.debug(`DeviceContext: Updating showActiveIndicator to ${value}`);
    try {
      const result = await displaySettingsAPI.update({ showActiveOnMap: value });
      _setShowActiveIndicator(result.settings.showActiveOnMap);
    } catch (err) {
      logger.error('DeviceContext: Failed to save display setting', err);
    }
  }, []);

  const setShowBatchTrackingButtons = useCallback(async (value: boolean): Promise<void> => {
    logger.debug(`DeviceContext: Updating showBatchTrackingButtons to ${value}`);
    try {
      const result = await displaySettingsAPI.update({ showBatchTrackingButtons: value });
      _setShowBatchTrackingButtons(result.settings.showBatchTrackingButtons);
    } catch (err) {
      logger.error('DeviceContext: Failed to save display setting', err);
    }
  }, []);

  const setShowTraceButton = useCallback(async (value: boolean): Promise<void> => {
    logger.debug(`DeviceContext: Updating showTraceButton to ${value}`);
    try {
      const result = await displaySettingsAPI.update({ showTraceButton: value });
      _setShowTraceButton(result.settings.showTraceButton);
    } catch (err) {
      logger.error('DeviceContext: Failed to save display setting', err);
    }
  }, []);

  const setGeofenceFitboundsPadding = useCallback(async (value: number): Promise<void> => {
    logger.debug(`DeviceContext: Updating geofenceFitboundsPadding to ${value}`);
    try {
      const result = await displaySettingsAPI.update({ geofenceFitboundsPadding: value });
      _setGeofenceFitboundsPadding(result.settings.geofenceFitboundsPadding);
    } catch (err) {
      logger.error('DeviceContext: Failed to save display setting', err);
    }
  }, []);

  const setGeofencingEnabled = useCallback(async (value: boolean): Promise<void> => {
    logger.debug(`DeviceContext: Updating geofencingEnabled to ${value}`);
    try {
      const result = await displaySettingsAPI.update({ geofencingEnabled: value });
      _setGeofencingEnabled(result.settings.geofencingEnabled);
    } catch (err) {
      logger.error('DeviceContext: Failed to save display setting', err);
    }
  }, []);

  const setShowCheckinSummary = useCallback(async (value: boolean): Promise<void> => {
    logger.debug(`DeviceContext: Updating showCheckinSummary to ${value}`);
    try {
      const result = await displaySettingsAPI.update({ showCheckinSummary: value });
      _setShowCheckinSummary(result.settings.showCheckinSummary);
    } catch (err) {
      logger.error('DeviceContext: Failed to save display setting', err);
    }
  }, []);

  const setShowDeviceStatusBoxes = useCallback(async (value: boolean): Promise<void> => {
    logger.debug(`DeviceContext: Updating showDeviceStatusBoxes to ${value}`);
    try {
      const result = await displaySettingsAPI.update({ showDeviceStatusBoxes: value });
      _setShowDeviceStatusBoxes(result.settings.showDeviceStatusBoxes === true);
    } catch (err) {
      logger.error('DeviceContext: Failed to save display setting', err);
    }
  }, []);

  const setBox2MqttEnabled = useCallback(async (value: boolean): Promise<void> => {
    logger.debug(`DeviceContext: Updating box2MqttEnabled to ${value}`);
    try {
      const result = await displaySettingsAPI.update({ box2MqttEnabled: value });
      _setBox2MqttEnabled(result.settings.box2MqttEnabled);
    } catch (err) {
      logger.error('DeviceContext: Failed to save display setting', err);
    }
  }, []);

  const setWebPushEnabled = useCallback(async (value: boolean): Promise<void> => {
    logger.debug(`DeviceContext: Updating webPushEnabled to ${value}`);
    try {
      const result = await displaySettingsAPI.update({ webPushEnabled: value });
      _setWebPushEnabled(result.settings.webPushEnabled);
    } catch (err) {
      logger.error('DeviceContext: Failed to save display setting', err);
    }
  }, []);

  const setEmailNotificationsEnabled = useCallback(async (value: boolean): Promise<void> => {
    logger.debug(`DeviceContext: Updating emailNotificationsEnabled to ${value}`);
    try {
      const result = await displaySettingsAPI.update({ emailNotificationsEnabled: value });
      _setEmailNotificationsEnabled(result.settings.emailNotificationsEnabled);
    } catch (err) {
      logger.error('DeviceContext: Failed to save display setting', err);
    }
  }, []);

  const setEmailAlarmTemplate = useCallback(async (value: string): Promise<void> => {
    logger.debug('DeviceContext: Updating emailAlarmTemplate');
    try {
      const result = await displaySettingsAPI.update({ emailAlarmTemplate: value });
      _setEmailAlarmTemplate(result.settings.emailAlarmTemplate);
    } catch (err) {
      logger.error('DeviceContext: Failed to save email alarm template', err);
      throw err;
    }
  }, []);

  const setRelayDnilConfig = useCallback(
    async (config: {
      relayDnilDeveui?: string;
      relayDnilChannels?: number;
      relaySilenceThresholdMs?: number;
    }): Promise<void> => {
      logger.debug('DeviceContext: Updating DNIL relay config', config);
      try {
        const result = await displaySettingsAPI.update(config);
        _setRelayDnilDeveui(result.settings.relayDnilDeveui);
        _setRelayDnilChannels(result.settings.relayDnilChannels);
        _setRelayDnilSilenceMs(result.settings.relaySilenceThresholdMs);
      } catch (err) {
        logger.error('DeviceContext: Failed to save DNIL relay config', err);
        throw err;
      }
    },
    []
  );

  /**
   * Fetch devices using the new service layer with type safety and error handling
   */
  const fetchDevices = useCallback(async (): Promise<void> => {
    if (!isAuthenticated) {
      logger.debug('DeviceContext: Skipping fetch - user not authenticated');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      logger.debug('DeviceContext: Fetching devices with new service layer...');
      const fetchedDevices = await deviceService.fetchDevices();

      setDevices(fetchedDevices);
      setLastUpdateTime(new Date());
      logger.debug(`DeviceContext: Successfully loaded ${fetchedDevices.length} devices`);
    } catch (error) {
      // Error is already processed by the service layer
      const processedError = error as LifeFinderError;

      logger.error('DeviceContext: Error fetching devices:', processedError);
      setError(processedError.userMessage);

      // Keep existing devices on error (don't clear the list)
      // This provides better UX during network issues
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  /**
   * Select a device with type safety
   */
  const selectDevice = useCallback(
    (deviceId: string | null): void => {
      logger.debug(`DeviceContext: Selecting device: ${deviceId}`);
      setSelectedDevice(deviceId);

      // Clear trace data when selecting different device
      if (deviceId !== selectedDevice) {
        setTraceData([]);
      }
    },
    [selectedDevice]
  );

  /**
   * Fetch trace data for a device using the new service layer
   */
  const fetchTrace = useCallback(async (deviceId: string): Promise<TracePoint[]> => {
    try {
      logger.debug(`DeviceContext: Fetching trace for device: ${deviceId}`);
      const trace = await deviceService.fetchTrace(deviceId);

      setTraceData(trace);
      logger.debug(`DeviceContext: Loaded ${trace.length} trace points`);

      return trace;
    } catch (error) {
      const processedError = error as LifeFinderError;
      logger.error('DeviceContext: Error fetching trace:', processedError);

      // Don't set global error for trace failures, let component handle it
      throw processedError;
    }
  }, []);

  /**
   * Device CRUD operations with type safety and error handling
   */
  const addDevice = useCallback(async (deviceData: DeviceCreateRequest): Promise<void> => {
    try {
      logger.debug('DeviceContext: Adding new device:', deviceData);
      await deviceService.addDevice(deviceData);

      // No optimistic insert: the backend does not return a device and it is not visible
      // until it reports a position. The periodic refresh will surface it. (M-4)
      logger.debug('DeviceContext: Device add request accepted');
    } catch (error) {
      const processedError = error as LifeFinderError;
      logger.error('DeviceContext: Error adding device:', processedError);
      throw processedError;
    }
  }, []);

  const updateDevice = useCallback(
    async (deviceId: string, updates: DeviceUpdateRequest): Promise<Device> => {
      try {
        logger.debug(`DeviceContext: Updating device ${deviceId}:`, updates);
        const updatedDevice = await deviceService.updateDevice(deviceId, updates);

        // Update local state
        setDevices((prev) =>
          prev.map((device) => (device.id === deviceId ? updatedDevice : device))
        );

        logger.debug(`DeviceContext: Successfully updated device: ${deviceId}`);
        return updatedDevice;
      } catch (error) {
        const processedError = error as LifeFinderError;
        logger.error('DeviceContext: Error updating device:', processedError);
        throw processedError;
      }
    },
    []
  );

  const deleteDevice = useCallback(
    async (deviceId: string): Promise<void> => {
      try {
        logger.debug(`DeviceContext: Deleting device: ${deviceId}`);
        await deviceService.deleteDevice(deviceId);

        // Remove from local state
        setDevices((prev) => prev.filter((device) => device.id !== deviceId));

        // Clear selection if deleted device was selected
        if (selectedDevice === deviceId) {
          setSelectedDevice(null);
          setTraceData([]);
        }

        logger.debug(`DeviceContext: Successfully deleted device: ${deviceId}`);
      } catch (error) {
        const processedError = error as LifeFinderError;
        logger.error('DeviceContext: Error deleting device:', processedError);
        throw processedError;
      }
    },
    [selectedDevice]
  );

  /**
   * Clear trace data
   */
  const clearTrace = useCallback((): void => {
    setTraceData([]);
  }, []);

  /**
   * Update current checkpoint with persistence
   */
  const updateCurrentCheckpoint = useCallback((checkpointId: string | null): void => {
    setCurrentCheckpoint(checkpointId);

    if (checkpointId) {
      window.localStorage.setItem('current-checkpoint', checkpointId);
    } else {
      window.localStorage.removeItem('current-checkpoint');
    }

    logger.debug(`DeviceContext: Updated current checkpoint to: ${checkpointId}`);
  }, []);

  /**
   * Get device by ID with type safety
   */
  const getDeviceById = useCallback(
    (deviceId: string): Device | undefined => {
      return devices.find((device) => device.id === deviceId);
    },
    [devices]
  );

  /**
   * Filter devices based on current filter criteria with type safety
   */
  const getFilteredDevices = useCallback((): Device[] => {
    if (!devices.length) return [];

    const now = Date.now() / 1000;
    const searchLower = searchQuery.toLowerCase();

    return devices.filter((device) => {
      // Search filter
      if (searchQuery) {
        const matchesSearch =
          (device.title && device.title.toLowerCase().includes(searchLower)) ||
          (device.text && device.text.toLowerCase().includes(searchLower)) ||
          (device.id && device.id.toLowerCase().includes(searchLower));

        if (!matchesSearch) return false;
      }

      // Alarm filter: when set to specific time, only show devices with alarms within the time limit
      // When set to 0 (∞), show all devices regardless of alarm status
      if (alarmFilter > 0) {
        // If device has no alarm, exclude it when a specific time filter is active
        if (!device.alarmTime) return false;

        // If device has alarm, check if it's within the time limit
        const alarmAge = now - device.alarmTime.getTime() / 1000;
        if (alarmAge > alarmFilter) return false;
      }
      // When alarmFilter === 0 (∞), don't filter by alarm at all - show all devices

      // Position filter
      if (positionFilter > 0 && device.positionTime) {
        const positionAge = now - device.positionTime.getTime() / 1000;
        if (positionAge > positionFilter) return false;
      }

      // Signal filter
      if (signalFilter > 0 && device.timestamp) {
        const signalAge = now - device.timestamp.getTime() / 1000;
        if (signalAge > signalFilter) return false;
      }

      // Category filter
      if (categoryFilter && categoryFilter !== 'all') {
        // If device has no category, only show it when filter is 'all' or 'lf_device'
        if (!device.deviceCategory) {
          if (categoryFilter !== 'lf_device') return false;
        } else {
          // Device has category, check if it matches the filter
          if (device.deviceCategory !== categoryFilter) return false;
        }
      }

      return true;
    });
  }, [devices, searchQuery, alarmFilter, positionFilter, signalFilter, categoryFilter]);

  const filteredDevices = getFilteredDevices();

  // Initial data fetch when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      logger.debug('DeviceContext: User authenticated, fetching initial data...');
      fetchDevices();
    }
  }, [isAuthenticated, fetchDevices]);

  // Fetch global display settings from backend whenever the user logs in.
  useEffect(() => {
    if (!isAuthenticated) return;
    displaySettingsAPI
      .get()
      .then((result) => {
        _setShowActiveIndicator(result.settings.showActiveOnMap);
        _setShowBatchTrackingButtons(result.settings.showBatchTrackingButtons);
        _setShowTraceButton(result.settings.showTraceButton ?? true);
        _setGeofenceFitboundsPadding(result.settings.geofenceFitboundsPadding);
        _setGeofencingEnabled(result.settings.geofencingEnabled);
        _setShowCheckinSummary(result.settings.showCheckinSummary);
        _setShowDeviceStatusBoxes(result.settings.showDeviceStatusBoxes === true);
        _setBox2MqttEnabled(result.settings.box2MqttEnabled);
        _setWebPushEnabled(result.settings.webPushEnabled);
        _setEmailNotificationsEnabled(result.settings.emailNotificationsEnabled);
        _setSmtpConfigured(result.settings.smtpConfigured ?? false);
        _setEmailAlarmTemplate(
          result.settings.emailAlarmTemplate ?? 'Larm {{action}} för: {{devices}}.'
        );
        _setRelayDnilDeveui(result.settings.relayDnilDeveui);
        _setRelayDnilChannels(result.settings.relayDnilChannels);
        _setRelayDnilSilenceMs(result.settings.relaySilenceThresholdMs);
        logger.debug(
          `DeviceContext: Loaded display settings — showActiveOnMap=${result.settings.showActiveOnMap}, showBatchTrackingButtons=${result.settings.showBatchTrackingButtons}, geofenceFitboundsPadding=${result.settings.geofenceFitboundsPadding}, geofencingEnabled=${result.settings.geofencingEnabled}, showCheckinSummary=${result.settings.showCheckinSummary}, box2MqttEnabled=${result.settings.box2MqttEnabled}`
        );
      })
      .catch((err) => {
        logger.error('DeviceContext: Failed to load display settings', err);
      });
  }, [isAuthenticated]);

  // Auto-refresh devices every 15 seconds
  useEffect(() => {
    if (!isAuthenticated) return;

    const interval = setInterval(() => {
      fetchDevices();
    }, 15000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, fetchDevices]);

  // Clear search query every 60 seconds (UX feature from original code)
  useEffect(() => {
    if (!searchQuery) return;

    const timer = setTimeout(() => {
      setSearchQuery('');
    }, 60000);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, setSearchQuery]);

  // Create context value with full TypeScript support
  const value: DeviceContextValue = {
    // State
    devices,
    filteredDevices,
    selectedDevice,
    currentCheckpoint,
    isLoading,
    error,

    // Filter state
    searchQuery,
    alarmFilter,
    positionFilter,
    signalFilter,
    categoryFilter,
    showActiveIndicator,
    showBatchTrackingButtons,
    showTraceButton,
    geofenceFitboundsPadding,
    geofencingEnabled,
    showCheckinSummary,
    showDeviceStatusBoxes,
    box2MqttEnabled,
    webPushEnabled,
    emailNotificationsEnabled,
    smtpConfigured,
    emailAlarmTemplate,
    relayDnilDeveui,
    relayDnilChannels,
    relayDnilSilenceMs,

    // Actions
    fetchDevices,
    selectDevice,
    addDevice,
    updateDevice,
    deleteDevice,
    fetchTrace,
    clearTrace,

    // Filter actions
    setSearchQuery,
    setAlarmFilter,
    setPositionFilter,
    setSignalFilter,
    setCategoryFilter,
    setShowActiveIndicator,
    setShowBatchTrackingButtons,
    setShowTraceButton,
    setGeofenceFitboundsPadding,
    setGeofencingEnabled,
    setShowCheckinSummary,
    setShowDeviceStatusBoxes,
    setBox2MqttEnabled,
    setWebPushEnabled,
    setEmailNotificationsEnabled,
    setEmailAlarmTemplate,
    setRelayDnilConfig,
    updateCurrentCheckpoint,

    // Utilities
    getDeviceById,
  };

  return <DeviceContext.Provider value={value}>{children}</DeviceContext.Provider>;
};

export default DeviceProvider;

/**
 * Benefits of this TypeScript migration:
 *
 * 1. Type Safety:
 *    - All props and state are typed
 *    - Compile-time error checking
 *    - IntelliSense support in IDEs
 *
 * 2. Error Handling:
 *    - Centralized error processing
 *    - User-friendly error messages
 *    - Automatic error reporting
 *
 * 3. Service Layer:
 *    - Separation of concerns
 *    - Reusable business logic
 *    - Consistent API interaction
 *
 * 4. Developer Experience:
 *    - Self-documenting code
 *    - Better refactoring support
 *    - Reduced runtime errors
 *
 * 5. Maintainability:
 *    - Clear interfaces and contracts
 *    - Easier debugging
 *    - Better team collaboration
 */
